// src/data/claimArrival.js
//
// Record 680 (Bob 2026-10-13, "address" the two gaps the 27 August member-to-LC audit left open -
// data\YCP_MEMBER_TO_LC_AUDIT_2026-08-27.md):
//   3.4 "NOTHING TELLS A LOCAL CHAIR THAT A CLAIM ARRIVED" - now its arrival tells him: when it is filed in his territory,
//       and when it is submitted to a representative (functions notifyClaimFiled, notifyClaimSubmitted; Record 680.1).
//   3.6 "NOTHING WARNS THE LC EITHER" - a claim that reaches the union after its filing window closed carries the flag on
//       arrival: in the notice, on the claims list row and on the claim. Information, never a block (claimRequirements E3):
//       the platform decides no timeliness question; the officer sees it the day the claim arrives.
// Pure; shared with functions\ by scripts\sync-shared-modules.js.

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const s = (v) => (v == null ? '' : String(v)).trim();

// The calendar date (Eastern, the union's clock) of an instant given as an ISO string or Date. '' when unknown.
export function etDate(instant) {
  if (!instant) return '';
  const d = instant instanceof Date ? instant : new Date(String(instant));
  if (isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch (e) {
    return d.toISOString().slice(0, 10);
  }
}

const dayNo = (iso) => Math.round(new Date(iso + 'T00:00:00Z').getTime() / 86400000);

// Where the claim stood against its filing window on the day it reached the union.
//   { known: false }                                   - no window on record (no agreement period or no occurrence date)
//   { known: true, late: true,  days: N, closes }      - arrived N days after the window closed
//   { known: true, late: false, days: N, closes }      - arrived with N days left (0 = the last day)
export function arrivalTiming(expiresAt, arrivedOn) {
  const closes = s(expiresAt), on = s(arrivedOn);
  if (!ISO_DATE.test(closes) || !ISO_DATE.test(on)) return { known: false };
  const diff = dayNo(on) - dayNo(closes);
  return diff > 0 ? { known: true, late: true, days: diff, closes } : { known: true, late: false, days: -diff, closes };
}

// When a claim reached the union: the day it was FILED (submittedAt - from then the officers of its territory read it),
// else the day it was submitted to a representative (carriageSince). '' for a draft or when neither is on record.
const toDate = (t) => (t && typeof t.toDate === 'function' ? t.toDate() : (t && typeof t.seconds === 'number' ? new Date(t.seconds * 1000) : t));
export function arrivedOn(claim) {
  const c = claim || {};
  if (s(c.status) === 'draft') return '';
  return etDate(toDate(c.submittedAt)) || (s(c.carriedBy) === 'representative' ? etDate(c.carriageSince) : '');
}

// The flag for a filed claim that reached the union after its filing window closed. null for a draft, for a claim that
// arrived inside its window, and when the window or the arrival is unknown.
export function lateArrivalFlag(claim) {
  const c = claim || {};
  const t = arrivalTiming(c.expiresAt, arrivedOn(c));
  if (!t.known || !t.late) return null;
  return { days: t.days, closes: t.closes,
    text: 'Arrived late: reached the union ' + t.days + ' day' + (t.days === 1 ? '' : 's') + ' after its filing window closed (' + t.closes + ')' };
}

export const NOT_A_BLOCK = 'The platform decides no timeliness question - that is for the parties. This is so you see it the day the claim arrives.';

// The notice a submission sends. `act` is the SUBMITTED act (carriage.buildAct); `claim` the claim as it stood.
// It goes to the office the claim went to: the General Chairperson's when it was submitted to her, or when the claim has
// no Local territory (unrouted); otherwise the Local Chairperson's, by the claim's scopeKey (the notification centre's
// read arm, firestore.rules /notifications).
// True when the claim was filed by someone other than its claimant (an officer filing for a member), by the two e-mails.
export function filedForAnother(c) {
  const f = s(c && c.filedBy).toLowerCase(), e = s(c && c.claimantEmail).toLowerCase();
  return !!f && f !== e;
}

export function arrivalNotice({ claimId, claim, act }) {
  const c = claim || {}, a = act || {};
  const scopeKey = s(c.scopeKey);
  const committee = s(c.committee || c.generalCommittee);
  const toGc = s(a.toRole) === 'general_chair' || !scopeKey || scopeKey.endsWith('|UNROUTED');
  const label = s(c.claimNumber) || s(c.title) || s(claimId);
  const who = s(c.claimantName) || 'A member';
  const arrived = act ? etDate(a.at) : arrivedOn(c);
  const review = s(a.purpose) === 'for_review';
  const t = arrivalTiming(c.expiresAt, arrived);
  const timing = !t.known
    ? 'The filing window could not be computed for this claim (no agreement period or no date of occurrence on record).'
    : t.late
      ? 'FILED LATE ON ARRIVAL: it reached the union ' + t.days + ' day' + (t.days === 1 ? '' : 's') + ' after its filing window closed on ' + t.closes + '. ' + NOT_A_BLOCK
      : (t.days === 0 ? 'Today is the last day of its filing window (' + t.closes + ').' : t.days + ' day' + (t.days === 1 ? '' : 's') + ' left in its filing window (closes ' + t.closes + ').');
  return {
    kind: 'claim_arrived', audience: toGc ? 'general_chair' : 'local_chair', recipientUid: null,
    claimId: s(claimId), committee, scopeKey, channel: 'in_app',
    actorName: who, actorRole: 'claimant', readAt: null,
    title: (t.known && t.late ? 'Claim arrived - FILED LATE: ' : 'Claim arrived: ') + label,
    // Bob 2026-10-14 (the walk-through): an officer filing FOR a member was reported as the member filing. filedBy is the
    // filer's own e-mail (claimPayload); when it is not the claimant's, the notice says who filed it and for whom.
    body: (act ? who + ' submitted this claim to ' + (s(a.toName) || 'you') + (review ? ' for review' : ' to take forward to the carrier')
      : (filedForAnother(c) ? 'A claim for ' + who + ' was filed by ' + s(c.filedBy) : who + ' filed this claim') + (toGc ? '' : ' in your territory'))
      + (arrived ? ' on ' + arrived : '') + (s(c.incidentDate) ? '. Date of occurrence: ' + s(c.incidentDate) : '') + '. ' + timing,
    lateOnArrival: !!(t.known && t.late),
  };
}
